import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { RouteComponentProps } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { getMeal, updateMeal } from '@/services/api/meals';
import { imageSchema } from '@/validation';

import Typography from '@/components/Typography';
import LoadData from '@/components/LoadData';
import If from '@/components/If';
import Loading from '@/components/Loading';
import Placeholder from '@/components/Placeholder';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ImageInputWithValidation } from '@/components/Input/ImageInput';

const formSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  price: z.number().min(0, 'Price must be greater than or equal to 0'),
  image: imageSchema
});

type FormValues = z.infer<typeof formSchema>;

interface MealFormProps {
  meal: any;
  onSubmit: (values: FormValues) => Promise<void>;
}

const MealForm: React.FC<MealFormProps> = ({ meal, onSubmit }) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: meal.title,
      description: meal.description,
      price: meal.price,
      image: { url: meal.imageUrl, thumbnailUrl: meal.thumbnailUrl }
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="Enter meal title" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea placeholder="Enter meal description" className="resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="price"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Price</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Enter meal price"
                  {...field}
                  onChange={(e) => field.onChange(parseFloat(e.target.value))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="image"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Image</FormLabel>
              <FormControl>
                <ImageInputWithValidation
                  onImageUploaded={(image) => {
                    form.setValue('image', image);
                  }}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={form.formState.isSubmitting || !form.formState.isDirty}>
          {form.formState.isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </form>
    </Form>
  );
};

const MealDetailPage: React.FC<RouteComponentProps<{ id: string }> & WrappedComponentProps> = ({ intl, match }) => {
  const mealId = match.params.id;

  return (
    <React.Fragment>
      <LoadData id={mealId} load={getMeal}>
        {({ value: meal, loading }) => (
          <If
            condition={loading}
            then={() => <Loading visible={loading} />}
            else={() => (
              <If
                condition={!meal}
                then={() => (
                  <Placeholder
                    className="h-full justify-center"
                    image="bell"
                    size="xs"
                    title="Not found"
                    description="Meal not found."
                  />
                )}
                else={() => {
                  async function handleSubmit(values: FormValues) {
                    try {
                      await updateMeal(mealId, values);
                    } catch (error) {
                      console.error('Failed to update meal:', error);
                    }
                  }

                  return (
                    <div className="mt-8">
                      <div className="flex justify-between">
                        <div>
                          <Typography is="h1" type="header">
                            {meal.title}
                          </Typography>
                          <Typography is="p" size="sm" className="text-gray-500">
                            Set the availability of this meal.
                          </Typography>
                        </div>
                      </div>

                      <div className="bg-white overflow-hidden shadow-sm rounded-lg p-4 mt-6">
                        <MealForm meal={meal} onSubmit={handleSubmit} />
                      </div>
                    </div>
                  );
                }}
              />
            )}
          />
        )}
      </LoadData>
    </React.Fragment>
  );
};

export default injectIntl(MealDetailPage);
